import { create } from 'zustand';

import { getView } from '../engine/prism.js';
import { setOverlay } from '../engine/host.js';
import { docIO } from '../lib/doc-io.js';
import {
  applyToPrefab,
  createPrefabFromEntity,
  createVariant,
  instanceOverrides,
  prefabPathFor,
  prefabRef,
  revertCommands,
  serializePrefab,
  unpackCommands,
  type OverrideItem,
  type PrefabDoc,
  type PrefabLoader,
} from '../lib/prefab.js';
import { selectionRoots } from './scene.js';
import { eventSocket } from '../lib/ws.js';
import { logEditor } from './console.js';
import { useProjectFiles } from './project-files.js';
import { useScene } from './scene.js';
import { useSceneSet } from './scene-set.js';
import { useWorkspace } from './workspace.js';

/**
 * Prefab documents the scene editor shows inside instances (cached, reloaded
 * when the file changes), the prefab actions (create, apply, revert, unpack,
 * variant) and prefab editing mode (a prefab opened in the Scene view on its
 * own, or in the context of the scene it was opened from).
 */

export type PrefabStageMode = 'isolated' | 'context';

interface PrefabsState {
  /** Loaded prefabs by path (null = missing / unreadable). */
  docs: Record<string, PrefabDoc | null>;
  /** Bumped when any prefab changes (memo key). */
  version: number;
  /** Prefab editing mode: the prefab open in the Scene view, the scene to return to. */
  stage: { prefab: string; returnTo: string | null; mode: PrefabStageMode } | null;

  /** Synchronous lookup; unknown paths are loaded and `undefined` is returned meanwhile. */
  loader: () => PrefabLoader;
  load: (path: string) => Promise<PrefabDoc | null>;
  invalidate: (path: string) => void;
  reset: () => void;
  /** Writes a prefab document (and updates the cache). */
  write: (path: string, doc: PrefabDoc) => Promise<boolean>;

  createFromSelection: (folder?: string) => Promise<string | null>;
  createFromEntity: (id: string, folder?: string) => Promise<string | null>;
  createVariantOf: (prefabPath: string) => Promise<string | null>;
  applyOverrides: (hostId: string, items?: readonly OverrideItem[]) => Promise<void>;
  revertOverrides: (hostId: string, items?: readonly OverrideItem[]) => void;
  unpack: (hostId: string, completely: boolean) => void;
  openStage: (prefab: string, mode?: PrefabStageMode) => Promise<void>;
  closeStage: () => Promise<void>;
}

const loading = new Set<string>();

function projectId(): number | null {
  return useWorkspace.getState().projectId;
}

function exists(path: string): boolean {
  return useProjectFiles.getState().entries.some((e) => e.path === path);
}

export const usePrefabs = create<PrefabsState>((set, get) => ({
  docs: {},
  version: 0,
  stage: null,

  loader: () => {
    const docs = get().docs;
    return (path) => {
      if (path in docs) return docs[path];
      if (!loading.has(path)) queueMicrotask(() => void get().load(path));
      return undefined;
    };
  },

  load: async (path) => {
    const pid = projectId();
    if (!pid || loading.has(path)) return get().docs[path] ?? null;
    loading.add(path);
    let doc: PrefabDoc | null = null;
    try {
      const parsed = JSON.parse(await docIO().read(pid, path)) as PrefabDoc;
      doc = parsed && Array.isArray(parsed.entities) ? parsed : null;
    } catch {
      doc = null;
    } finally {
      loading.delete(path);
    }
    if (projectId() === pid) set((s) => ({ docs: { ...s.docs, [path]: doc }, version: s.version + 1 }));
    return doc;
  },

  invalidate: (path) => {
    if (!(path in get().docs)) return;
    set((s) => {
      const docs = { ...s.docs };
      delete docs[path];
      return { docs, version: s.version + 1 };
    });
  },

  reset: () => set({ docs: {}, version: get().version + 1, stage: null }),

  write: async (path, doc) => {
    const pid = projectId();
    if (!pid) return false;
    const text = serializePrefab(doc);
    try {
      await docIO().write(pid, path, text);
    } catch (error) {
      logEditor('error', `Could not save ${path}: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
    setOverlay(path, text);
    set((s) => ({ docs: { ...s.docs, [path]: doc }, version: s.version + 1 }));
    void getView('scene')?.fileChanged(path);
    useProjectFiles.getState().refreshSoon();
    return true;
  },

  createFromSelection: async (folder) => {
    const { doc, selection } = useScene.getState();
    if (!doc || selection.length === 0) return null;
    let last: string | null = null;
    for (const id of selectionRoots(doc, selection)) last = await get().createFromEntity(id, folder);
    return last;
  },

  createFromEntity: async (id, folder = 'prefabs') => {
    const scene = useScene.getState();
    const doc = scene.doc;
    const entity = doc?.entities.find((e) => e.id === id);
    if (!doc || !entity) return null;
    const path = prefabPathFor(folder, entity.name || id, exists);
    const made = createPrefabFromEntity(doc, id, path);
    if (!made) return null;
    if (!(await get().write(path, made.prefab))) return null;
    scene.applyBatch(made.commands);
    logEditor('info', `Created prefab ${path} from '${entity.name || id}'.`);
    return path;
  },

  createVariantOf: async (prefabPath) => {
    const base = get().docs[prefabPath] ?? (await get().load(prefabPath));
    const folder = prefabPath.includes('/') ? prefabPath.slice(0, prefabPath.lastIndexOf('/')) : '';
    const variant = createVariant(prefabPath, base);
    const path = prefabPathFor(folder, variant.name ?? 'Variant', exists);
    if (!(await get().write(path, variant))) return null;
    logEditor('info', `Created prefab variant ${path} of ${prefabPath}.`);
    return path;
  },

  applyOverrides: async (hostId, items) => {
    const scene = useScene.getState();
    const host = scene.doc?.entities.find((e) => e.id === hostId);
    const ref = prefabRef(host);
    if (!host || !ref) return;
    const prefab = get().docs[ref.path] ?? (await get().load(ref.path));
    if (!prefab) {
      logEditor('error', `Cannot apply: prefab ${ref.path} is missing.`);
      return;
    }
    const result = applyToPrefab(host, prefab, items ?? null, get().loader());
    if (!(await get().write(ref.path, result.prefab))) return;
    if (result.commands.length) scene.applyBatch(result.commands);
  },

  revertOverrides: (hostId, items) => {
    const scene = useScene.getState();
    const host = scene.doc?.entities.find((e) => e.id === hostId);
    if (!host) return;
    const commands = revertCommands(host, items ?? null, get().loader());
    if (commands.length) scene.applyBatch(commands);
  },

  unpack: (hostId, completely) => {
    const scene = useScene.getState();
    if (!scene.doc) return;
    const commands = unpackCommands(scene.doc, hostId, get().loader(), completely);
    if (commands.length === 0) {
      logEditor('warn', 'Cannot unpack: the prefab is missing or still loading.');
      return;
    }
    scene.applyBatch(commands);
  },

  openStage: async (prefab, mode = 'isolated') => {
    const scene = useScene.getState();
    const current = get().stage;
    const returnTo = current ? current.returnTo : scene.path;
    await scene.flush();
    set({ stage: { prefab, returnTo, mode } });
    await scene.open(prefab);
    // In context: the scene it was opened from stays visible around it.
    useSceneSet.getState().setContext(mode === 'context' && returnTo ? [returnTo] : []);
    useWorkspace.getState().setView('scene');
  },

  closeStage: async () => {
    const stage = get().stage;
    if (!stage) return;
    await useScene.getState().flush();
    set({ stage: null });
    useSceneSet.getState().setContext([]);
    if (stage.returnTo) await useScene.getState().open(stage.returnTo);
  },
}));

/** Overrides of one instance (for the Inspector header / Hierarchy menu). */
export function overridesOf(hostId: string): OverrideItem[] {
  const host = useScene.getState().doc?.entities.find((e) => e.id === hostId);
  return host ? instanceOverrides(host, null, usePrefabs.getState().loader()) : [];
}

// Prefab files changed on disk: reload them (instances re-expand).
eventSocket.subscribe((event) => {
  if (event.type === 'file.changed' && typeof event.path === 'string' && event.path.endsWith('.prefab.json')) {
    usePrefabs.getState().invalidate(event.path);
  }
});

// Another project: its prefabs and prefab mode are gone.
useWorkspace.subscribe((state, prev) => {
  if (state.projectId !== prev.projectId) usePrefabs.getState().reset();
});
